// Mode display names mapping (consistent with Process Flow tab)
export const MODE_NAMES = {
  'defense-cooperation': 'Negotiated Procurement – Defense Cooperation Agreement and Inventory-Based Items',
  'defense-inv': 'Negotiated Procurement - Defense Inventory-Based',
  'sti': 'Direct Procurement for STI',
  'emergency': 'Negotiated Procurement - Emergency Cases',
  'two-failed-bidding': 'Negotiated Procurement - Two Failed Biddings',
  'takeover': 'Negotiated Procurement - Take-Over Contracts',
  'agency-to-agency': 'Negotiated Procurement - Agency-to-Agency',
  'highly-technical-consultants': 'Negotiated Procurement - Highly Technical Consultants',
  'direct-contracting': 'Direct Contracting',
  'scientific-scholarly': 'Negotiated Procurement - Scientific/Scholarly/Artistic Work',
  'direct-sales': 'Direct Sales',
  'adjacent-contiguous': 'Negotiated Procurement - Adjacent/Contiguous',
  'ngo-participation': 'Negotiated Procurement - NGO Participation',
  'community-participation': 'Negotiated Procurement - Community Participation',
  'farmers-fisherfolks': 'Negotiated Procurement - Direct from Farmers/Fisherfolks',
  'pol': 'Negotiated Procurement - POL & Online Subscriptions',
  'sagip-saka': 'Sagip Saka Procurement (GPPB Resolution No. 08-2025)',
  'international-events': 'Negotiated Procurement for International Events, Conferences, Summits, or Activities Hosted by the Philippine Government (GPPB Resolution No. 01-2026)',
  'un-agencies': 'Negotiated Procurement - UN Agencies/IOs/IFIs',
  'lease-property': 'Negotiated Procurement - Lease of Real Property',
  'lease-venue': 'Negotiated Procurement - Lease of Venue',
  'direct-retail': 'Negotiated Procurement - Direct Retail Purchase of POL, Electronic Charging Devices, and Online Subscription',
  'limited-source-bidding': 'Limited Source Bidding',
  'repeat-order': 'Repeat Order',
  'competitive-dialogue': 'Competitive Dialogue',
  'small-value': 'Small Value Procurement',
  'direct-acquisition': 'Direct Acquisition',
  'unsolicited': 'Unsolicited Offer with Bid Matching',
  'competitive-bidding': 'Competitive Bidding',
  'cse-psdbm': 'Procurement of CSE from PS-DBM'
};

// Complete scenario mapping from Assessment Results Mapping Tool v2
// Each scenario has: modeId, score, trigger condition, verification criteria, ABC threshold
export const PROCUREMENT_SCENARIOS = {
  goods: [
    { 
      scenario: 'All goods and general support services, except CSEs not under centralized procurement',
      modeId: 'competitive-bidding', 
      score: 15.5, 
      condition: 'None', 
      verify: 'None',
      abcThreshold: null,
      riskLevel: 'Low'
    },
    { 
      scenario: 'Highly specialized or complex goods with very few known sources',
      modeId: 'limited-source-bidding', 
      score: 37.5, 
      condition: 'Requirement is highly specialized/complex and obtainable only from limited suppliers',
      verify: 'Existence of authoritative/self-generated list of capable suppliers; justification of limited sources',
      abcThreshold: null,
      riskLevel: 'Medium'
    },
    { 
      scenario: 'Complex ICT/systems goods where specifications cannot be fully defined',
      modeId: 'competitive-dialogue', 
      score: 25.5, 
      condition: 'Technical specifications cannot be fully developed at project start',
      verify: 'Goods are complex/innovative; two-stage dialogue justified',
      abcThreshold: null,
      riskLevel: 'Medium–High'
    },
    { 
      scenario: 'New technology concept in goods proposed unsolicited',
      modeId: 'unsolicited', 
      score: 16.5, 
      condition: 'HoPE determines the offer involves new tech; subject to bid matching',
      verify: 'Compliance with new concept/technology criteria; completeness of original offer',
      abcThreshold: null,
      riskLevel: 'High'
    },
    { 
      scenario: 'Proprietary or IP-protected equipment/software',
      modeId: 'direct-contracting', 
      score: 48, 
      condition: 'Goods are proprietary/IP-protected; only one legitimate source',
      verify: 'Proof of proprietary/IP status; no viable alternatives',
      abcThreshold: null,
      riskLevel: 'Medium'
    },
    { 
      scenario: 'Critical component required to preserve contractor warranty',
      modeId: 'direct-contracting', 
      score: 48, 
      condition: 'Component required by manufacturer/contractor to maintain warranty',
      verify: 'Contract terms showing requirement; technical justification',
      abcThreshold: null,
      riskLevel: 'Medium'
    },
    { 
      scenario: 'Exclusive distributor with no lower-priced substitutes',
      modeId: 'direct-contracting', 
      score: 22, 
      condition: 'Exclusive dealer/manufacturer; no cheaper substitutes',
      verify: 'Authentic distributorship documents; market scan showing absence of alternatives',
      abcThreshold: null,
      riskLevel: 'Medium'
    },
    { 
      scenario: 'Very low-value off-the-shelf goods',
      modeId: 'direct-acquisition', 
      score: 28.5, 
      condition: 'ABC ≤ ₱200,000 (adjustable); simple/off-the-shelf items',
      verify: 'ABC within ceiling; reputable source; COA-acceptable invoice',
      abcThreshold: 200000,
      riskLevel: 'Low'
    },
    { 
      scenario: 'Small simple services (e.g., repairs, minor maintenance)',
      modeId: 'direct-acquisition', 
      score: 22, 
      condition: 'Services ≤ threshold; simple in nature',
      verify: 'Fit within Sec. 32 parameters; proper authority; COA compliance',
      abcThreshold: 200000,
      riskLevel: 'Low–Medium'
    },
    { 
      scenario: 'Need to replenish goods recently procured via bidding',
      modeId: 'repeat-order', 
      score: 70, 
      condition: 'Subsequent order within limits (≤25%, ≤6 months)',
      verify: 'Item divisibility; time/quantity caps; price ≤ original & market',
      abcThreshold: null,
      riskLevel: 'Medium'
    },
    { 
      scenario: 'Small goods requirement within SVP threshold',
      modeId: 'small-value', 
      score: 47.5, 
      condition: 'ABC ≤ SVP ceiling (national/LGU)',
      verify: 'At least 3 RFQs sent; proper posting; no splitting',
      abcThreshold: 2000000,
      riskLevel: 'Medium'
    },
    { 
      scenario: 'Calamity – emergency supplies',
      modeId: 'emergency', 
      score: 51, 
      condition: 'Imminent danger or need to restore vital services using goods',
      verify: 'Calamity/emergency declaration; link of goods to urgent need',
      abcThreshold: null,
      riskLevel: 'High'
    },
    { 
      scenario: 'Proprietary / exclusive goods',
      modeId: 'scientific-scholarly', 
      score: 75.5, 
      condition: 'Item available only from particular source or fits exclusive/scientific criteria',
      verify: 'Proof of exclusivity or special nature (IP, certifications)',
      abcThreshold: null,
      riskLevel: 'Medium'
    },
    { 
      scenario: 'From another agency with stocks',
      modeId: 'agency-to-agency', 
      score: 46, 
      condition: 'Servicing government agency can legally/technically supply goods',
      verify: 'Mandate and capacity certification; no subcontracting',
      abcThreshold: null,
      riskLevel: 'Medium'
    },
    { 
      scenario: 'Major defense equipment, defense-related inventory items',
      modeId: 'defense-cooperation', 
      score: 46, 
      condition: 'Exclusive for defense item or equipment under modernization or national security criteria',
      verify: 'DND approvals; inclusion in defense/modernization programs',
      abcThreshold: null,
      riskLevel: 'High'
    },
    { 
      scenario: 'Farmers/fisherfolk produce',
      modeId: 'community-participation', 
      score: 43.5, 
      condition: 'Direct purchase from farmers/fisherfolk',
      verify: 'Beneficiary identification; fair pricing; community alignment',
      abcThreshold: 5000000,
      riskLevel: 'Low'
    },
    { 
      scenario: 'Labor-only contracting for services related to community-based projects',
      modeId: 'community-participation', 
      score: 42, 
      condition: 'Services rendered by Unorganized Community Social Groups (UCGS)',
      verify: 'Beneficiary identification; fair pricing; community alignment',
      abcThreshold: 1000000,
      riskLevel: 'Low'
    },
    { 
      scenario: 'UN/IO-supplied health/education goods',
      modeId: 'un-agencies', 
      score: 46, 
      condition: 'Goods fall under allowed UN/IO/IFI categories',
      verify: 'Category verification; advantage over standard procurement',
      abcThreshold: null,
      riskLevel: 'Medium'
    },
    { 
      scenario: 'POL, electronic charging devices, or online subscriptions',
      modeId: 'direct-retail', 
      score: 75.5, 
      condition: 'Petroleum Fuel, Oil and Lubricant Products (POL), electronic charging devices, or online subscriptions needed for daily operations, not in PS-DBM. This mode is ONLY applicable to these specific items.',
      verify: 'Delegation papers; operational necessity; item must be POL, electronic charging device, or online subscription only',
      abcThreshold: 1000000,
      riskLevel: 'Medium'
    },
    { 
      scenario: 'Non-CSE delivered to another agency via bidding',
      modeId: 'direct-sales', 
      score: 46, 
      condition: 'Non-CSE supplied under a bid-out contract of another agency',
      verify: 'Delivery acceptance recorded; ≤6 months; same/lower price; supplier still capable',
      abcThreshold: null,
      riskLevel: 'Medium'
    },
    { 
      scenario: 'R&D project needing specialized supplies/equipment',
      modeId: 'sti', 
      score: 43.5, 
      condition: 'Items for actual, exclusive, direct use in R&D',
      verify: 'Confirm eligible R&D activity; capability of supplier; direct R&D use',
      abcThreshold: null,
      riskLevel: 'Medium–High'
    },
    { 
      scenario: 'Other analogous S&T/innovation-related items',
      modeId: 'sti', 
      score: 42, 
      condition: 'Item analogous to R&D/innovation uses',
      verify: 'Justification of analogy; alignment with innovation policy',
      abcThreshold: null,
      riskLevel: 'Medium'
    },
    { 
      scenario: 'Negotiated Procurement for International Events, Conferences, Summits, or Activities Hosted by the Philippine Government (e.g., ASEAN, APEC, SEA Games, UN meetings)',
      modeId: 'international-events', 
      score: 46, 
      condition: 'International event formally hosted by PH Government; existing modes impracticable due to security, protocol, or operational requirements',
      verify: 'Documentation of international event; justification of impracticability; value-for-money assessment; compliance with security and diplomatic protocols',
      abcThreshold: null,
      riskLevel: 'Medium–High'
    }
  ],
  infrastructure: [
    { 
      scenario: 'All infrastructure projects',
      modeId: 'competitive-bidding', 
      score: 46, 
      condition: 'None',
      verify: 'None',
      abcThreshold: null,
      riskLevel: 'Low'
    },
    { 
      scenario: 'Highly specialized infrastructure involving national security',
      modeId: 'limited-source-bidding', 
      score: 75.5, 
      condition: 'Infra project is highly specialized and affects national security',
      verify: 'Security justification and proof of specialized nature of works',
      abcThreshold: null,
      riskLevel: 'High'
    },
    { 
      scenario: 'Complex infrastructure (ICT infra, design-sensitive works)',
      modeId: 'competitive-dialogue', 
      score: 15.5, 
      condition: 'Need for dialogue to refine design/value engineering',
      verify: 'Project complexity; inability to define specs upfront; suitability of two-stage bidding',
      abcThreshold: null,
      riskLevel: 'High'
    },
    { 
      scenario: 'Small infrastructure works within SVP ceiling',
      modeId: 'small-value', 
      score: 37.5, 
      condition: 'Same rules applied to small infra projects',
      verify: 'ABC fits SVP limit; simplified RFQ appropriate',
      abcThreshold: 2000000,
      riskLevel: 'Medium–High'
    },
    { 
      scenario: 'Additional section beside ongoing project',
      modeId: 'adjacent-contiguous', 
      score: 25.5, 
      condition: 'Works physically adjacent/contiguous to an ongoing competitively-bid project',
      verify: 'Physical contiguity; related scope; no slippage; same/lower unit prices',
      abcThreshold: null,
      riskLevel: 'Low'
    },
    { 
      scenario: 'Gov agency to implement works',
      modeId: 'agency-to-agency', 
      score: 22, 
      condition: 'Another gov agency has legal works mandate and track record',
      verify: 'Mandate; ≥50% project magnitude experience; absorptive capacity',
      abcThreshold: null,
      riskLevel: 'Medium'
    },
    { 
      scenario: 'Heritage structure conservation',
      modeId: 'scientific-scholarly', 
      score: 46, 
      condition: 'Work involves conservation of historical/cultural structure',
      verify: 'Heritage significance documentation; specialized restoration skills',
      abcThreshold: null,
      riskLevel: 'Medium'
    },
    { 
      scenario: 'Community-level simple infra',
      modeId: 'community-participation', 
      score: 51, 
      condition: 'Simple infra suitable for community execution',
      verify: 'Simplicity; OCSG/UCSG identity; capability and roles',
      abcThreshold: 10000000,
      riskLevel: 'Low'
    }
  ],
  consulting: [
    { 
      scenario: 'All consulting services',
      modeId: 'competitive-bidding', 
      score: 15.5, 
      condition: 'None',
      verify: 'None',
      abcThreshold: null,
      riskLevel: 'Low'
    },
    { 
      scenario: 'Complex consulting (policy reform, large systems, PPP-like)',
      modeId: 'competitive-dialogue', 
      score: 37.5, 
      condition: 'Innovative/complex intellectual services requiring iterative development',
      verify: 'Dialogue materially improves quality; meets Sec. 29 triggers',
      abcThreshold: null,
      riskLevel: 'Medium–High'
    },
    { 
      scenario: 'New technology concept in consulting proposed unsolicited',
      modeId: 'unsolicited', 
      score: 25.5, 
      condition: 'Same "new technology" trigger as goods, applied to consulting',
      verify: 'Offer is genuinely new/innovative; passes HoPE evaluation',
      abcThreshold: null,
      riskLevel: 'High'
    },
    { 
      scenario: 'Small consulting assignment within SVP threshold',
      modeId: 'small-value', 
      score: 16.5, 
      condition: 'ABC within ceiling; simple TOR',
      verify: 'TOR quality; 3 RFPs; qualitative/price evaluation',
      abcThreshold: 2000000,
      riskLevel: 'Medium'
    },
    { 
      scenario: 'Individual expert – proprietary / confidential work',
      modeId: 'highly-technical-consultants', 
      score: 48, 
      condition: 'Work is highly technical, proprietary, confidential, or policy-level',
      verify: 'Trust-and-confidence justification; 1-year term limits',
      abcThreshold: null,
      riskLevel: 'High'
    },
    { 
      scenario: 'Academic or legal research',
      modeId: 'scientific-scholarly', 
      score: 47.5, 
      condition: 'Work fits scientific, academic, scholarly, or legal service definition',
      verify: 'Proof of academic/research/legal nature; expert credentials',
      abcThreshold: null,
      riskLevel: 'Medium'
    },
    { 
      scenario: 'Calamity or emergency consulting services',
      modeId: 'emergency', 
      score: 75.5, 
      condition: 'Imminent danger or need to restore vital services requiring urgent consulting expertise',
      verify: 'Documented emergency; urgency justification; calamity declaration if applicable',
      abcThreshold: null,
      riskLevel: 'High'
    },
    { 
      scenario: 'Defense-related consultancy',
      modeId: 'defense-cooperation', 
      score: 75.5, 
      condition: 'Consultancy under defense cooperation / national security programs',
      verify: 'DND clearance; link to defense modernization',
      abcThreshold: null,
      riskLevel: 'High'
    },
    { 
      scenario: 'Continuation of existing study',
      modeId: 'adjacent-contiguous', 
      score: 46, 
      condition: 'Additional consulting linked to an ongoing competitively-bid consultancy',
      verify: 'Related scope; continuity advantage; amount ≤ existing contract',
      abcThreshold: null,
      riskLevel: 'Low'
    },
    { 
      scenario: 'UN/IO expert team',
      modeId: 'un-agencies', 
      score: 43.5, 
      condition: 'Specialized consulting from UN or IO teams',
      verify: 'Specialization/technology aspect; comparative advantage',
      abcThreshold: null,
      riskLevel: 'Medium'
    },
    { 
      scenario: 'Commissioned development of new gov\'t solutions',
      modeId: 'sti', 
      score: 75.5, 
      condition: 'Commissioned pre-commercial/startup-developed outputs',
      verify: 'Written commissioning; nature of output; IP/ownership alignment',
      abcThreshold: null,
      riskLevel: 'High'
    }
  ]
};

// Initial wizard data state structure
export const INITIAL_WIZARD_DATA = {
  // Phase 1: Project Information
  projectName: '',
  
  // Phase 2: Entity Type
  entityType: '',
  
  // Phase 3: Procurement Type Selection Method
  typeSelectionMethod: '', // 'manual' or 'ai'
  projectDescription: '', // For AI recommendation
  procurementType: '', // 'goods', 'infrastructure', 'consulting', 'mixed'
  aiRecommendedType: null,
  aiConfidence: null,
  aiTopMatches: [],
  
  // Phase 4: Contract Value
  contractValue: '',
  
  // Phase 3.5: For Mixed procurement - Primary Purpose Determination
  primaryPurpose: null, // 'goods', 'infrastructure', 'consulting', or 'unable' (only for mixed type)
  
  isCSE: null, // For goods: is it CSE or non-CSE?
  cseJustification: { // Justification for NOT using PS-DBM for CSE items
    unavailableInCatalog: false,
    cannotMeetSpecs: false,
    insufficientQuantity: false,
    inefficiency: false,
    impracticality: false,
    economicNonViability: false
  },
  isCommunityBased: null, // For infrastructure projects: is it community-based?
  applicableThresholds: [], // Auto-detected thresholds
  suggestedModes: [], // Modes suggested based on thresholds
  
  // Phase 5: Special Conditions Assessment
  specialConditionChoice: null, // 'none' or specific condition ID
  specialConditionSubOption: null, // For conditions with sub-options
  selectedScenario: '', // Scenario text
  selectedConditionText: '', // Main Special Condition Triggered text
  selectedModeForBoost: null, // The mode ID that should get a ranking boost
  marketCondition: '', // 'robust', 'limited', 'single'
  abcThresholdConfirmed: null, // true/false
  
  // Phase 6: Main Mode Selection
  mainMode: '',
  rankedModes: [], // AI-ranked modes
  rankingLoading: false,
  
  // Phase 6: Market Assessment
  marketAvailability: '', // 'robust', 'limited', 'single-none'
  supplierInput: '', // 'standard', 'dialogue', 'limited-sources'
  marketUncertaintyOptions: {
    smallValueThreshold: false,
    breakIntoLots: false,
    directContracting: false,
    unsolicitedBidMatching: false
  },
  applicableModes: [], // Filtered modes based on procurement type
  
  // Phase 6: Exception Category
  exceptionCategory: '',
  thresholdMet: null,
  justificationReady: null
};
